import { z } from 'zod/v4';
import { FieldType } from '@/components/table/_fieldDefinitions/FieldType.ts';
import { phoneNumberValidationSchema } from '@/schemas/ValidationSchema.ts';
import { PhoneNumberDto } from '@/types/PhoneNumberDto.ts';
import { BillField } from '@/features/dashboard/bookingChanges/types/dto/BillField.ts';
import { ClientBillField } from '@/features/dashboard/bookingChanges/types/dto/ClientBillField.ts';
import { ParticipantBillField } from '@/features/dashboard/bookingChanges/types/dto/ParticipantBillField.ts';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import { TOGGLE_NO, TOGGLE_YES } from './fieldTypes';

/**
 * Formularz edycji pojedynczej pozycji szkicu.
 *
 * Jeden kształt danych obsługuje wszystkie rodzaje pól — kontrolka zapisuje się
 * albo w `value` (tekst, liczba jako tekst, identyfikator wariantu, `Tak`/`Nie`,
 * wartości wielokrotne po przecinku), albo w `date`. Dzięki temu typ formularza
 * jest stały i nie trzeba przełączać `useForm` przy zmianie wiersza.
 *
 * Kwoty trzymamy jako tekst, a nie liczbę: `register` zwraca string, a operator
 * wpisuje przecinek dziesiętny — konwersję robi `parseAmount`.
 */
export interface EditFieldFormData {
  value: string;
  date: Date | null;
  /** Wybór wielokrotny wariantów cennika — identyfikatory, nie nazwy. */
  values: string[];
  /** Adres zapisującego: trzy pola `AddressDto` zamiast jednej sklejonej linijki. */
  street: string;
  postalCode: string;
  city: string;
  /** Imię i nazwisko osobno — kontrakt paczki i tak niesie je jako dwa pola. */
  firstName: string;
  lastName: string;
  /** Telefon: kierunkowy i numer osobno, tak jak w kontrakcie. */
  phoneNumber: PhoneNumberDto;
  /**
   * Opis pozycji ręcznej — etykieta jej wiersza na rachunku. Wymagany i
   * niepusty, tak jak w konstruktorze encji `ManualLineChangeItem`.
   */
  description: string;
  /** Ręczne nadpisanie kwoty katalogowej — znacznik ✎ przy wierszu. */
  priceOverridden: boolean;
  price: string;
  priceNote: string;
  /**
   * Waluta kwoty. Wybiera ją operator **wyłącznie przy pozycji ręcznej** — ta
   * jako jedyna nie ma wiersza rachunku, z którego walutę dałoby się wziąć.
   * Pozostałe wiersze dziedziczą walutę swojej pozycji księgi.
   */
  currency: Currency;
}

/**
 * Wszystko, czego formularz potrzebuje o edytowanym wierszu: typ pola oraz
 * kształt samego wiersza, którego `FieldType` nie wyraża i wyrażać nie powinien.
 */
export interface EditFieldShape {
  type: FieldType;
  /** Pola kontraktu opisywane przez wiersz (`BillLineRefDto.fields`). */
  fields: BillField[];
  /** Pozycja ręczna — opis i kwota są polami samej pozycji, a nie osoby. */
  manual: boolean;
}

/** Pole formularza, które obsługuje jedno pole kontraktu, wraz z jego walidacją. */
export interface BillFieldInput {
  name: 'firstName' | 'lastName' | 'street' | 'postalCode' | 'city';
  label: string;
  placeholder?: string;
  /** Komunikat błędu albo `null`, gdy wartość jest w porządku. */
  validate: (value: string) => string | null;
}

/** Kod pocztowy w zapisie `NN-NNN` — tak jak w pozostałych formularzach aplikacji. */
const POSTAL_CODE_PATTERN = /^\d{2}-\d{3}$/;

const required =
  (message: string) =>
  (value: string): string | null =>
    value.trim() === '' ? message : null;

/**
 * Pola kontraktu, o które formularz pyta osobnym inputem.
 *
 * Uczestnik i zapisujący dzielą wartości `FIRST_NAME` i `LAST_NAME`, więc jeden
 * wpis wystarcza obu — i dlatego kluczem jest `${BillField}`, a nie `BillField`:
 * dwa enumy o wspólnych wartościach dają w `Record` klucze, których TypeScript
 * nie potrafi ze sobą zestawić. Pola spoza tej mapy (`EMAIL`, `PHONE`,
 * `BIRTH_DATE`) stoją w wierszu samotnie i idą wspólnym `value` — kontrolkę
 * wybiera im `FieldType`.
 */
const BILL_FIELD_INPUTS: Partial<Record<`${BillField}`, BillFieldInput>> = {
  [ParticipantBillField.FIRST_NAME]: {
    name: 'firstName',
    label: 'Imię',
    validate: required('Podaj imię'),
  },
  [ParticipantBillField.LAST_NAME]: {
    name: 'lastName',
    label: 'Nazwisko',
    validate: required('Podaj nazwisko'),
  },
  [ClientBillField.STREET]: {
    name: 'street',
    label: 'Ulica i numer',
    validate: required('Podaj ulicę i numer'),
  },
  [ClientBillField.POSTAL_CODE]: {
    name: 'postalCode',
    label: 'Kod pocztowy',
    placeholder: '00-000',
    validate: (value) =>
      POSTAL_CODE_PATTERN.test(value.trim())
        ? null
        : 'Podaj kod pocztowy w formacie 00-000',
  },
  [ClientBillField.CITY]: {
    name: 'city',
    label: 'Miejscowość',
    validate: required('Podaj miejscowość'),
  },
};

/**
 * Inputy wiersza wielopolowego — jeden wiersz rachunku bywa projekcją kilku pól
 * kontraktu („Imię i nazwisko” to dwa, adres trzy) i wtedy formularz pyta o
 * każde osobno, zamiast rozklejać jeden napis po pierwszej spacji (na czym
 * wykładało się „Anna Maria Kowalska”). Wiersz jednopolowy zwraca pustą listę:
 * jego wartość niesie `value`, a kontrolkę wybiera typ pola.
 */
export const perFieldInputs = (fields: BillField[]): BillFieldInput[] =>
  fields.length > 1
    ? fields.flatMap((field) => BILL_FIELD_INPUTS[field] ?? [])
    : [];

/** „1 250,50” / „1250.50” → `1250.5`; tekst bez liczby → `null`. */
export const parseAmount = (text: string): number | null => {
  const normalized = text.replace(/\s/g, '').replace(',', '.');

  if (normalized === '') {
    return null;
  }

  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : null;
};

const AMOUNT_MESSAGE = 'Podaj kwotę liczbą, np. 240 albo 240,50';

export const buildEditFieldSchema = ({
  type,
  fields,
  manual,
}: EditFieldShape) =>
  z
    .object({
      value: z.string(),
      date: z.date().nullable(),
      values: z.array(z.string()),
      street: z.string(),
      postalCode: z.string(),
      city: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      phoneNumber: z.object({ dialCode: z.string(), number: z.string() }),
      description: z.string(),
      priceOverridden: z.boolean(),
      price: z.string(),
      priceNote: z.string(),
      currency: z.enum(Currency),
    })
    .superRefine((data, ctx) => {
      perFieldInputs(fields).forEach((input) => {
        const message = input.validate(data[input.name]);
        if (message) {
          ctx.addIssue({ code: 'custom', path: [input.name], message });
        }
      });

      if (manual) {
        if (data.description.trim() === '') {
          ctx.addIssue({
            code: 'custom',
            path: ['description'],
            message: 'Podaj opis pozycji',
          });
        }

        const amount = parseAmount(data.value);
        if (amount === null) {
          ctx.addIssue({
            code: 'custom',
            path: ['value'],
            message: AMOUNT_MESSAGE,
          });
          // Pozycja na zero nic do rachunku nie wnosi — dopisanie jej byłoby
          // pustym wierszem, a nie kosztem ani zniżką.
        } else if (amount === 0) {
          ctx.addIssue({
            code: 'custom',
            path: ['value'],
            message: 'Kwota pozycji nie może być zerowa',
          });
        }
      } else if (
        type === FieldType.MONEY_VALUE &&
        parseAmount(data.value) === null
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['value'],
          message: AMOUNT_MESSAGE,
        });
      }

      // Numer waliduje ta sama reguła, co pozostałe formularze aplikacji —
      // przepisany komunikat rozjechałby się z nią przy pierwszej zmianie.
      if (type === FieldType.PHONE_NUMBER) {
        const result = phoneNumberValidationSchema.safeParse(data.phoneNumber);
        if (!result.success) {
          ctx.addIssue({
            code: 'custom',
            path: ['phoneNumber'],
            message: result.error.issues[0].message,
          });
        }
      }

      if (type === FieldType.DATE && data.date === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['date'],
          message: 'Wybierz datę',
        });
      }

      if (
        type === FieldType.CHECKBOX &&
        data.value !== TOGGLE_YES &&
        data.value !== TOGGLE_NO
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['value'],
          message: 'Wybierz „Tak” albo „Nie”',
        });
      }

      if (data.priceOverridden && parseAmount(data.price) === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['price'],
          message: AMOUNT_MESSAGE,
        });
      }
    });
